"use client";

import { motion } from "framer-motion";
import { Search, CircleCheckBig } from "lucide-react";
import { getViewModeChrome } from "../view-mode-chrome";

interface TickerSearchSuccessProps {
  result?: {
    status: string;
    results?: {
      [key: string]: Array<{
        ticker: string;
        name: string;
        market: string;
        exchange: string;
        tradingview_symbol: string;
        polygon_validated: boolean;
      }>;
    };
  };
  viewMode?: string;
}

export function TickerSearchSuccess({
  result,
  viewMode,
}: TickerSearchSuccessProps) {
  const chrome = getViewModeChrome(viewMode);
  // Extract all ticker results from the nested structure
  const allTickers = result?.results
    ? Object.values(result.results).flat()
    : [];

  const tickerCount = allTickers.length;
  const title = `Found ${tickerCount} ticker${tickerCount !== 1 ? "s" : ""}`;

  // Prepare a concise inline chip list for subtitle (deduped, max 6)
  const deduped = Array.from(
    new Map(allTickers.map((t) => [t.ticker, t])).values()
  );
  const display = deduped.slice(0, 6);
  const extraCount = deduped.length - display.length;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="w-full"
    >
      <div className={`rounded-md border ${chrome.card} overflow-hidden`}>
        <div className="px-3 py-2 flex items-center justify-between gap-3 min-w-0">
          <div className="flex items-center gap-2.5 min-w-0">
            {/* Search icon to match other success bars */}
            <Search
                className={`w-3.5 h-3.5 ${chrome.iconMuted} shrink-0`}
                strokeWidth={2}
              />

            {/* Text content */}
            <div className="flex flex-col min-w-0 max-w-[calc(100%-3rem)]">
              <span className={`text-[13px] font-semibold ${chrome.text} truncate`}>
                {title}
              </span>
              <div className="text-xs leading-tight min-w-0">
                <div className="relative w-full min-w-0 overflow-hidden">
                  <div className="flex items-center gap-1.5 text-[11px] text-foreground/80 font-mono whitespace-nowrap overflow-x-auto w-full">
                    {display.map((t) => (
                      <span
                        key={t.ticker}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-muted/40 border border-border"
                        title={t.name}
                      >
                        {t.ticker}
                        {t.polygon_validated ? (
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500" />
                        ) : null}
                      </span>
                    ))}
                    {extraCount > 0 && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm border border-dashed border-border text-muted-foreground/70">
                        +{extraCount} more
                      </span>
                    )}
                    <div className="pointer-events-none sticky right-0 h-full w-8 bg-gradient-to-l from-background to-transparent -ml-8" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Success checkmark */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
          >
            <CircleCheckBig className="h-4 w-4  text-green-500" />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
